import { useMemo } from 'react';
import type { ReactNode } from 'react';

import { BannerStatus } from 'ui-components/components/banner-message/banner-enums';
import { BannerMessageV2 } from 'ui-components/components/banner-message/banner-message-v2';
import { Button } from 'ui-components/components/button';
import { Text, TextType } from 'ui-components/components/text';
import { EventSourceStrings } from '../../../../tracking/tracking-constants';
import { WalletRegistrationOrUpdateButton as UntypedWalletRegistrationOrUpdateButton } from '../../../../wallet/components/wallet-registration-or-update-button';
import messages from '../messages';
import style from './carriers-wallet.module.less';

type WalletRegistrationOrUpdateButtonProps = {
  trackingId: string;
  walletId: string;
  children: (options: { openModal?: () => void; isWalletRegistered?: boolean; hasWalletUpdate?: boolean }) => ReactNode;
  modalProps?: Record<string, unknown>;
  shouldModalBeOpenAtStart?: boolean;
  initialStepId?: string;
};

// WalletRegistrationOrUpdateButton is untyped JS — cast to preserve children render prop
const WalletRegistrationOrUpdateButton =
  UntypedWalletRegistrationOrUpdateButton as React.ComponentType<WalletRegistrationOrUpdateButtonProps>;

type CarrierRegistrationErrorProps = {
  isFraudError: boolean;
  supportUrl: string;
  openWalletPaymentUpdate: () => void;
  truncatedRegistrationErrorMessage: string;
  walletId: string;
};

export const CarrierRegistrationError = ({
  isFraudError,
  supportUrl,
  openWalletPaymentUpdate,
  truncatedRegistrationErrorMessage,
  walletId,
}: CarrierRegistrationErrorProps) => {
  const values = useMemo(
    () => ({
      addressLink: (chunks: ReactNode) => (
        <Button className={style['inline-registration-link']} onClick={openWalletPaymentUpdate} theme='link'>
          {chunks}
        </Button>
      ),
      error: truncatedRegistrationErrorMessage,
      updateLink: (chunks: ReactNode) => (
        <WalletRegistrationOrUpdateButton trackingId={EventSourceStrings.SETTINGS_CARRIERS_PAGE_BANNER} walletId={walletId}>
          {({ openModal }) => (
            <Button className={style['inline-registration-link']} onClick={openModal} theme='link'>
              {chunks}
            </Button>
          )}
        </WalletRegistrationOrUpdateButton>
      ),
    }),
    [openWalletPaymentUpdate, truncatedRegistrationErrorMessage, walletId],
  );

  return (
    <BannerMessageV2
      bannerContainerClassName={style['registration-error']}
      className={style['registration-error-container']}
      content={
        isFraudError ? (
          <Text
            formattedMessageValues={{
              supportLink: (chunks: ReactNode) => (
                <Button href={supportUrl} theme='link'>
                  {chunks}
                </Button>
              ),
            }}
            textType={TextType.Paragraph}
            value={messages.registrationFraudError}
          />
        ) : (
          <Text formattedMessageValues={values} textType={TextType.Paragraph} value={messages.registrationAddressError} />
        )
      }
      status={BannerStatus.Error}
    />
  );
};
